import { useApi } from "@akina/infra";
import { useLocaleStore } from "@akina/presentation";
import { useMutation } from "@tanstack/react-query";
import type { CheckoutFormValues } from "@/domain/checkout";
import { useIntl } from "@/infra/locale";
import { useTreatmentSelectionStore } from "@/infra/stores/treatmentSelectionStore";
import type { WebshopInvitePatientRequestApi } from "types/schemas";

export function parseDDMMYYYY(value: string): Date | null {
  const parts = value.split(".");
  if (parts.length !== 3) return null;
  const [day, month, year] = parts.map(Number);
  if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) return null;
  if (month < 1 || month > 12) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return date;
}

function getErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  if ("response" in error && error.response && typeof error.response === "object" && "status" in error.response) {
    return error.response.status as number;
  }
  if ("status" in error) return error.status as number;
  return undefined;
}

export function useWebshopInvitePatient() {
  const { api } = useApi();
  const { formatMessage } = useIntl();
  const locale = useLocaleStore(s => s.locale);
  const selectedTemplateKey = useTreatmentSelectionStore(s => s.selectedTemplateKey);
  const customDescription = useTreatmentSelectionStore(s => s.customDescription);
  const defaultCouponCode = formatMessage({ id: "checkoutPage.paymentStep.productCard.defaultCouponCode" });

  return useMutation({
    retry: (failureCount, error) => getErrorStatus(error) === 429 && failureCount < 4,
    retryDelay: failureCount => Math.pow(2, failureCount) * 1000,
    mutationFn: async (values: CheckoutFormValues) => {
      if (!selectedTemplateKey) throw new Error("No treatment template selected");
      const dob = parseDDMMYYYY(values.dateOfBirth);
      if (!dob) throw new Error("Invalid date of birth");
      const { token } = await api.webshop.getToken();
      const payload: WebshopInvitePatientRequestApi = {
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        date_of_birth: dob.toISOString(),
        treatment_template_key: selectedTemplateKey,
        language: locale,
        webshop_token: token,
        custom_description: customDescription || null,
        coupon_code: defaultCouponCode || null,
      };
      return api.webshop.invitePatient(payload);
    },
  });
}
